import { graphql } from "babel-plugin-relay/macro";
import { useMutation } from "relay-hooks";
import { useViewerDecks } from "../viewerQuery";
import { useCardKnowMutation, useCardKnowMutation$data } from "./__generated__/useCardKnowMutation.graphql";
import { useGetState } from "components/ReduxProvider";
import { RecordSourceSelectorProxy } from "relay-runtime";
import { CARD_LEARNED_THRESHOLD } from "../../sharedJs__generated/constants";

const mutation = graphql`
  mutation useCardKnowMutation(
    $id: ID!
    $know: Boolean!
    $loopFilter: LoopFilter
    $filter: String
    $noResponse: Boolean
    $isLast: Boolean
  ) {
    cardKnow(id: $id, know: $know, filter: $filter, noResponse: $noResponse, isLast: $isLast) {
      ...loopQuery_loop @arguments(loopFilter: $loopFilter)
    }
  }
`;

export const useCardKnow = () => {
  const { deck } = useViewerDecks();
  const [mutate, { loading }] = useMutation<useCardKnowMutation>(mutation);
  const loopFilter = useGetState("loopFilter");

  const cardKnow = (variables: useCardKnowMutation["variables"], knownCount?: number) => {
    const updater = (store: RecordSourceSelectorProxy<useCardKnowMutation$data>, isOptimistic: boolean = false) => {
      const { know, isLast } = variables;
      const deckRec = store.get(deck?.id || "");
      let loop = deckRec?.getLinkedRecords("loop", { loopFilter });
      if (!deckRec) return;

      // the last card is considered as "known" for some time even if swiped left
      const shouldUpdateStats = (know || isLast) && Number(deck?.stats.unknown) > 0;
      if (shouldUpdateStats) {
        const stats = deckRec.getLinkedRecord("stats");
        const isLearned = know && knownCount && knownCount + 1 >= CARD_LEARNED_THRESHOLD;
        if (isLearned) {
          stats?.setValue(Number(deck?.stats.completed) + 1, "completed");
        } else {
          stats?.setValue(Number(deck?.stats.known) + 1, "known");
        }
        stats?.setValue(Number(deck?.stats.unknown) - 1, "unknown");
      }

      // always remove the card except when we get actual data from BE
      if (isOptimistic || variables.noResponse) {
        loop = loop?.filter((card) => card?.getDataID() !== variables.id);
        deckRec.setLinkedRecords(loop, "loop", { loopFilter });
      }
    };

    const filter = loopFilter?.includeKnown ? loopFilter.filterId : undefined;
    return mutate({
      variables: { ...variables, ...{ loopFilter }, filter },
      optimisticUpdater: (store: RecordSourceSelectorProxy<useCardKnowMutation$data>) => {
        updater(store, true);
      },
      updater: (store: RecordSourceSelectorProxy<useCardKnowMutation$data>) => {
        updater(store, false);
      }
    });
  };
  return { cardKnow, loading };
};
